import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const FAQ = () => {
  const { t } = useTranslation();

  const faqQuestions = [
    { question: t("faq.q1"), answer: t("faq.a1") },
    { question: t("faq.q2"), answer: t("faq.a2") },
    { question: t("faq.q3"), answer: t("faq.a3") },
    { question: t("faq.q4"), answer: t("faq.a4") },
    { question: t("faq.q5"), answer: t("faq.a5") },
    { question: t("faq.q6"), answer: t("faq.a6") },
  ];

  return (
    <section id="faq" className="py-24 lg:py-32 bg-band-sage relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none text-primary" style={{ backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`, backgroundSize: '32px 32px' }} />
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative">
        <motion.div className="text-center max-w-3xl mx-auto mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <motion.span className="inline-block px-4 py-2 rounded-full bg-accent/20 text-primary font-medium text-sm mb-6 border border-accent/30" initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4 }}>
            {t("faq.badge")}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold mb-6 tracking-tight">
            {t("faq.headline")}{" "}
            <span className="text-gradient-sage">{t("faq.headlineHighlight")}</span>
          </h2>
          <p className="text-lg lg:text-xl text-muted-foreground">{t("faq.subheadline")}</p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4" onValueChange={(v) => { if (v) { const idx = Number(v.replace("item-", "")); trackEvent("faq_open", { question: faqQuestions[idx]?.question }); } }}>
            {faqQuestions.map((faq, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.1 }}>
                <AccordionItem value={`item-${index}`} className="bg-surface/50 border border-border/50 rounded-2xl px-6 data-[state=open]:bg-surface data-[state=open]:border-primary/20 transition-all duration-300">
                  <AccordionTrigger className="text-start font-semibold text-foreground hover:text-primary py-5 [&[data-state=open]]:text-primary">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {faq.answer}
                    {(index === 2 || index === 3) && <> <Link to="/terms#fair-use" className="text-primary hover:underline">{t("faq.fairUse")}</Link></>}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>

        <motion.div className="text-center mt-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
          <p className="text-muted-foreground mb-4">{t("faq.stillQuestions")}</p>
          <a href="mailto:contact@getpiefolio.com" className="inline-flex items-center gap-2 text-primary font-semibold hover:underline">
            {t("faq.contactSupport")}
            <span>→</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
